import { useCallback, useRef, useState } from 'react'

/**
 * Manages controlled vs uncontrolled state in a single hook.
 *
 * The controlled/uncontrolled decision is made once at mount time (stored in a ref)
 * so the component never silently transitions between modes across renders.
 *
 * @param controlled - The externally provided value. Pass `undefined` for uncontrolled mode.
 * @param defaultValue - The initial value used in uncontrolled mode.
 * @returns A `[value, setValue]` tuple. `setValue` is a no-op in controlled mode.
 */
export function useControllableState<T>(controlled: T | undefined, defaultValue: T): [T, (next: T) => void] {
  const isControlledRef = useRef(controlled !== undefined)
  const [uncontrolled, setUncontrolled] = useState<T>(defaultValue)

  const value = isControlledRef.current ? (controlled as T) : uncontrolled

  const setValue = useCallback((next: T) => {
    if (!isControlledRef.current) {
      setUncontrolled(next)
    }
  }, [])

  return [value, setValue]
}
